import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SectionDecor } from "@/components/SectionDecor";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { PageIntro } from "@/components/PageIntro";
import { RealisticCityMap } from "@/components/RealisticCityMap";
import { Button } from "@/components/ui/button";
import { caCities, company } from "@/data/site";
import { getGroupedCities } from "@/data/cityRegions";
import areasHero from "@/assets/heroes/areas-rooftops.jpg";

export const Route = createFileRoute("/areas-served")({
  head: () => ({
    meta: [
      { title: "Service Areas – McKay Roofing & Solar" },
      { name: "description", content: "McKay Roofing & Solar serves homeowners across California. Explore our service areas by state and city." },
      { name: "keywords", content: "roofing service areas California, solar installation service areas, McKay Roofing & Solar" },
      { property: "og:title", content: "Service Areas – McKay Roofing & Solar" },
      { property: "og:description", content: "Roofing and solar service across California." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/areas-served" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/areas-served" }],
  }),
  component: AreasPage,
});

function AreasPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <PageIntro variant="map" />
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={areasHero} alt="Aerial drone view of Southern California rooftops served by McKay Roofing & Solar" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36 lg:px-8 lg:py-44">
            <h1 className="text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">Where We Work</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              McKay Roofing &amp; Solar proudly serves homeowners across California. Choose your state below to explore local roofing and solar service in your city.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Title + description above the map */}
        <section className="relative bg-background">
          <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Southern California</div>
            <h2 className="mt-3 font-serif text-4xl font-bold text-primary sm:text-5xl">Explore Our Service Map</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Serving {caCities.length} cities across California. Explore the interactive map and tap a city to view local roofing and solar service details.
            </p>
          </div>
        </section>

        {/* Immersive realistic map experience (all breakpoints) */}
        <section className="relative block w-full overflow-hidden bg-[#f6f7f9]">
          {/* atmospheric glows */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-40 top-1/4 h-[600px] w-[600px] rounded-full bg-primary/10 blur-[160px]" />
            <div className="absolute -right-40 bottom-0 h-[720px] w-[720px] rounded-full bg-secondary/15 blur-[180px]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(255,255,255,0.55)_100%)]" />
            {/* subtle grid overlay */}
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(0,51,102,.55) 1px, transparent 1px), linear-gradient(90deg, rgba(0,51,102,.55) 1px, transparent 1px)",
                backgroundSize: "48px 48px",
                maskImage:
                  "radial-gradient(ellipse at center, black 30%, transparent 75%)",
              }}
            />
          </div>

          <div className="relative z-10 mx-auto flex w-full max-w-[1600px] items-center px-3 py-6 sm:px-6 md:h-[85vh] xl:px-10">
            <div className="w-full" style={{ perspective: "2200px" }}>
              <div className="w-full origin-top transition-transform duration-700 ease-out md:[transform:rotateX(6deg)]">
                <RealisticCityMap />
              </div>
            </div>
          </div>
        </section>




        {/* City cards + California hub below the map */}
        <section className="relative overflow-hidden bg-muted/40">
          <SectionDecor variant="house" side="right" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Southern California</div>
                <h2 className="mt-3 font-serif text-4xl font-bold text-primary sm:text-5xl">California Service Cities</h2>
                <p className="mt-3 text-muted-foreground">
                  Serving {caCities.length} cities across California. Pick your city to see local roofing and solar service details, or head to the California hub for the full overview.
                </p>
              </div>
              <Link
                to="/areas/california"
                className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5"
              >
                Visit California hub <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-12 space-y-14">
              {getGroupedCities().map((region) => (
                <div key={region.id}>
                  <div className="flex flex-col gap-3 border-b border-border pb-4 md:flex-row md:items-end md:justify-between">
                    <div>
                      <h3 className="font-serif text-2xl font-bold text-primary sm:text-3xl">
                        {region.label}
                        <span className="ml-3 align-middle text-xs font-bold uppercase tracking-[0.15em] text-secondary">
                          {region.cities.length} cities
                        </span>
                      </h3>
                      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{region.blurb}</p>
                    </div>
                    <Link
                      to="/areas/hubs/$hub"
                      params={{ hub: region.id }}
                      className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5"
                    >
                      Visit {region.label} hub <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>


                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {region.cities.map((city) => (
                      <Link
                        key={city.slug}
                        to="/areas/california/$city"
                        params={{ city: city.slug }}
                        className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg"
                      >
                        <div className="flex items-center gap-2 text-primary">
                          <MapPin className="h-4 w-4 text-secondary" />
                          <span className="font-serif text-xl font-bold">{city.name}</span>
                        </div>
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{city.blurb}</p>
                        <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-primary transition-colors group-hover:text-secondary">
                          View city <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        <CTABlock
          title="Don't see your city listed? We probably still cover it."
          subtitle="Give us a call and we'll let you know right away whether your address falls in our service area."
        />
      </main>
      <SiteFooter />
    </div>
  );
}
